/**
 * <product-models> — the models block of the product gallery.
 *
 * The block ships collapsed, as on marineserre.com: the box is clipped to the
 * width of one portrait, so only the column of faces shows. The button at the
 * foot of the column opens it, revealing the line that goes with each face —
 * "Lucy measures 177cm and wears a size S" — and closes it again.
 *
 * **It also decides which model the gallery shows**, and says so: the gallery
 * listens for `product:model` and unhides that model's photographs. The rule,
 * in order:
 *
 *   1. the first model, in the order the media put them — which is what the
 *      server already rendered, so arriving on a product costs nothing here;
 *   2. unless the URL ends in `#w` or `#m`, in which case the first model of
 *      that gender wins. A fragment never reaches the server, which is why this
 *      part cannot be done in Liquid;
 *   3. a click on a portrait, at any time.
 *
 * The gender of a model is read from the markup, where Liquid has already
 * reduced two sources to one letter: the `w-` or `m-` segment the catalogue
 * puts in front of some alts, and failing that the `genre` of the metaobject.
 *
 * The portraits are fragment links to the first picture of their model, so
 * without this file the block stays a plain column of working links, the first
 * one bordered, and every photograph of the product is visible — nothing is
 * hidden by the server that JavaScript alone could bring back. Same reason the
 * opener ships `hidden`.
 *
 * Markup contract:
 *
 *   <product-models>
 *     [data-models-box]      the clipped box; takes nothing, the state lives
 *                            on the host as `is-expanded`
 *     [data-models-inner]    the element the open width lands on, through
 *                            `--models-open-width` on the host
 *     [data-models-item]     one per model, in order; takes `is-active`, which
 *                            the first one ships with. Carries
 *                            data-models-name and data-models-gender ("w",
 *                            "m" or empty)
 *     [data-models-link]     the link inside it, the thing one clicks
 *     [data-models-toggle]   the opener, ships with `hidden`; carries
 *                            data-label-show and data-label-hide, both
 *                            translated in Liquid, and its own icon element
 *     [data-models-icon]     optional, the "+" that becomes "−"
 *     [data-models-close]    optional, the cross of the open box; ships with
 *                            `hidden`, shown only while the box is open
 *
 *   and, beside the block, the gallery's own [data-gallery-stack], whose width
 *   the open box takes.
 *
 * Emits `product:model` (bubbling) with `{ name }` whenever the chosen model
 * changes, including once on arrival when the hash asked for another one.
 *
 * Class names are free: nothing here reads one.
 */

class ProductModels extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-models-item]')];

    /* The click is caught on the link rather than on the row: the row is only
       a box, and the link is what a keyboard reaches. The jump to the picture
       stays the gallery's business, and nothing is prevented here. */
    this.items.forEach((item) => {
      item.querySelector('[data-models-link]')?.addEventListener('click', () => this.select(item));
    });

    /* The open box spans the picture beside it. That width has to be measured:
       the block is a flex item of no width, so a percentage inside it resolves
       against nothing, and the width of the image stack is out of reach of CSS.
       The site measures the same thing in its own script. */
    this.inner = this.querySelector('[data-models-inner]');
    this.stack = this.parentElement?.querySelector('[data-gallery-stack]');
    this.onResize = () => this.measure();
    this.measure();
    window.addEventListener('resize', this.onResize, { passive: true });

    /* The hash is read once, on arrival. Anything else than `#w` or `#m` is
       none of our business — a fragment link to a picture, for one.
       This announcement reaches a gallery that is already listening: the
       snippet loads `product-gallery.js` before `product-models.js` and both
       are deferred, so the gallery is defined, upgraded and listening by the
       time this line runs. Swapping those two script tags would silently lose
       the hash. */
    const asked = this.fromHash();
    if (asked && asked !== this.items[0]) this.select(asked);

    this.toggle = this.querySelector('[data-models-toggle]');
    if (!this.toggle) return;

    this.icon = this.querySelector('[data-models-icon]');
    this.close = this.querySelector('[data-models-close]');

    this.toggle.hidden = false;
    this.setExpanded(false);
    this.toggle.addEventListener('click', () => {
      this.setExpanded(!this.expanded);

      /* Below the breakpoint the open box is a panel over the page and the
         opener is no longer shown in it: the focus moves to the cross rather
         than falling to the body with the button it was on. */
      if (this.expanded && this.close && this.toggle.getClientRects().length === 0) this.close.focus();
    });

    /* The panel of the mobile layout veils the whole page with the host
       itself: a click that lands on the host, outside the box, is a click on
       the veil. On desktop the host is a column of no width, which nothing
       can land on. Escape closes it too, wherever the focus is inside. */
    this.addEventListener('click', (event) => {
      if (this.expanded && event.target === this) {
        this.setExpanded(false);
        this.toggle.focus();
      }
    });
    this.addEventListener('keydown', (event) => {
      if (this.expanded && event.key === 'Escape') {
        this.setExpanded(false);
        this.toggle.focus();
      }
    });

    /* The cross closes and hands the focus back to the opener: it disappears
       with the box, and a focus left on a hidden button falls to the body. */
    this.close?.addEventListener('click', () => {
      this.setExpanded(false);
      this.toggle.focus();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  /**
   * The model the URL asks for, or nothing. `#w` and `#m` only; the letter is
   * compared to what Liquid put on each row, which already holds the alt's own
   * segment when it has one and the gender of the metaobject otherwise.
   */
  fromHash() {
    const asked = window.location.hash.replace('#', '').toLowerCase();
    if (asked !== 'w' && asked !== 'm') return null;

    return this.items.find((item) => item.dataset.modelsGender === asked) || null;
  }

  measure() {
    if (!this.inner || !this.stack) return;

    const width = this.stack.getBoundingClientRect().width;
    if (width) this.style.setProperty('--models-open-width', `${Math.round(width)}px`);
  }

  /**
   * Hands the black border to one row and tells the gallery whose photographs
   * to show. Announced even when the row is already the active one on arrival
   * by hash, so that the gallery and the border can never disagree.
   */
  select(item) {
    this.items.forEach((other) => {
      other.classList.toggle('is-active', other === item);
      /* The choice is told, not only drawn: `aria-current` follows the border. */
      const link = other.querySelector('[data-models-link]');
      if (!link) return;
      if (other === item) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });

    this.dispatchEvent(
      new CustomEvent('product:model', {
        bubbles: true,
        detail: { name: item.dataset.modelsName || '' },
      })
    );
  }

  setExpanded(expanded) {
    this.expanded = expanded;
    this.classList.toggle('is-expanded', expanded);
    this.toggle.setAttribute('aria-expanded', expanded);

    /* Both labels come from the locale files through the markup: no string of
       this block is built here. */
    const label = expanded ? this.toggle.dataset.labelHide : this.toggle.dataset.labelShow;
    if (label) this.toggle.setAttribute('aria-label', label);
    if (this.icon) this.icon.textContent = expanded ? '−' : '+';
    if (this.close) this.close.hidden = !expanded;
    this.setModal(expanded);
  }

  /**
   * Below the breakpoint the open box is a panel over the whole page, and
   * looks modal: it has to be one for the keyboard and the screen reader too.
   * The box takes the dialog role and its heading as name; everything else
   * in the page becomes `inert`, so that Tab and the virtual cursor cannot
   * wander under the veil (the pattern of show-looks, `setPageInert`). On
   * desktop the box is a column in the flow, and nothing of this applies.
   * Only what this method made inert is released: the banner's copies carry
   * their own `inert`, which must stay.
   */
  setModal(expanded) {
    const box = this.querySelector('[data-models-box]');
    const title = this.querySelector('.product-models__title');
    const modal = expanded && window.matchMedia('screen and (max-width: 900px)').matches;

    if (box) {
      if (modal) {
        box.setAttribute('role', 'dialog');
        box.setAttribute('aria-modal', 'true');
        if (title && title.id) box.setAttribute('aria-labelledby', title.id);
      } else {
        box.removeAttribute('role');
        box.removeAttribute('aria-modal');
        box.removeAttribute('aria-labelledby');
      }
    }

    (this.inerted || []).forEach((element) => element.removeAttribute('inert'));
    this.inerted = [];
    if (!modal) return;

    let node = this;
    while (node.parentElement && node !== document.body) {
      [...node.parentElement.children].forEach((sibling) => {
        if (sibling === node || sibling.hasAttribute('inert')) return;
        sibling.setAttribute('inert', '');
        this.inerted.push(sibling);
      });
      node = node.parentElement;
    }
  }
}

if (!customElements.get('product-models')) {
  customElements.define('product-models', ProductModels);
}
